import { del, get, post, put } from '../../ReaderlyHttpClient';
import {
  DeliveryInformationData,
  DeliveryInformationPayload,
  GoogleAccountLinkPasswordData,
  GoogleLoginData,
  GoogleLoginResultData,
  LoginData,
  LoginResponse,
  PasswordRecoveryConfirmData,
  PasswordRecoveryConfirmResultData,
  PasswordRecoveryRequestData,
  PasswordRecoveryRequestResultData,
  PasswordRecoveryTokenData,
  PasswordRecoveryTokenValidationData,
  RegisterData,
  TelegramLoginData,
  TelegramLoginNonceData,
  USERS_SERVICE_URLS,
  UserInfoData,
} from './Data';
import IUsersService from './IUsersService';

class UsersService implements IUsersService {
  async login(data: LoginData): Promise<LoginResponse> {
    // A wrong-credentials login legitimately returns 401, but that's not a "session
    // expired" event — suppress the global handler so callers can show their own message.
    return await post<LoginResponse, LoginData>(USERS_SERVICE_URLS.LOGIN, data, undefined, {
      suppressGlobalError: true,
    });
  }

  async loginWithGoogle(data: GoogleLoginData): Promise<GoogleLoginResultData> {
    return await post<GoogleLoginResultData, GoogleLoginData>(USERS_SERVICE_URLS.GOOGLE_LOGIN, data, undefined, {
      suppressGlobalError: true,
    });
  }

  async confirmGoogleLinkWithPassword(data: GoogleAccountLinkPasswordData): Promise<LoginResponse> {
    return await post<LoginResponse, GoogleAccountLinkPasswordData>(
      USERS_SERVICE_URLS.GOOGLE_LINK_CONFIRM_PASSWORD,
      data,
      undefined,
      { suppressGlobalError: true }
    );
  }

  async requestGoogleLinkPasswordRecovery(data: GoogleLoginData): Promise<PasswordRecoveryRequestResultData> {
    return await post<PasswordRecoveryRequestResultData, GoogleLoginData>(
      USERS_SERVICE_URLS.GOOGLE_LINK_PASSWORD_RECOVERY_REQUEST,
      data,
      undefined,
      { suppressGlobalError: true }
    );
  }

  async loginWithTelegram(data: TelegramLoginData): Promise<LoginResponse> {
    return await post<LoginResponse, TelegramLoginData>(USERS_SERVICE_URLS.TELEGRAM_LOGIN, data, undefined, {
      suppressGlobalError: true,
    });
  }

  async linkTelegram(data: TelegramLoginData): Promise<UserInfoData> {
    return await post<UserInfoData, TelegramLoginData>(USERS_SERVICE_URLS.TELEGRAM_LINK, data, undefined, {
      suppressGlobalError: true,
    });
  }

  async linkGoogle(data: GoogleLoginData): Promise<UserInfoData> {
    return await post<UserInfoData, GoogleLoginData>(USERS_SERVICE_URLS.GOOGLE_LINK, data, undefined, {
      suppressGlobalError: true,
    });
  }

  async getTelegramLoginNonce(): Promise<TelegramLoginNonceData> {
    return await get<TelegramLoginNonceData>(USERS_SERVICE_URLS.TELEGRAM_LOGIN_NONCE);
  }

  async register(data: RegisterData): Promise<LoginResponse> {
    return await post<LoginResponse, RegisterData>(USERS_SERVICE_URLS.REGISTER, data, undefined, {
      suppressGlobalError: true,
    });
  }

  async requestPasswordRecovery(data: PasswordRecoveryRequestData): Promise<PasswordRecoveryRequestResultData> {
    return await post<PasswordRecoveryRequestResultData, PasswordRecoveryRequestData>(
      USERS_SERVICE_URLS.PASSWORD_RECOVERY_REQUEST,
      data,
      undefined,
      { suppressGlobalError: true }
    );
  }

  async validatePasswordRecoveryToken(data: PasswordRecoveryTokenData): Promise<PasswordRecoveryTokenValidationData> {
    return await post<PasswordRecoveryTokenValidationData, PasswordRecoveryTokenData>(
      USERS_SERVICE_URLS.PASSWORD_RECOVERY_VALIDATE,
      data,
      undefined,
      { suppressGlobalError: true }
    );
  }

  async confirmPasswordRecovery(data: PasswordRecoveryConfirmData): Promise<PasswordRecoveryConfirmResultData> {
    return await post<PasswordRecoveryConfirmResultData, PasswordRecoveryConfirmData>(
      USERS_SERVICE_URLS.PASSWORD_RECOVERY_CONFIRM,
      data,
      undefined,
      { suppressGlobalError: true }
    );
  }

  async getCurrentUser(): Promise<UserInfoData> {
    return await get<UserInfoData>(USERS_SERVICE_URLS.CURRENT_USER);
  }

  async addDeliveryInformation(data: DeliveryInformationPayload): Promise<DeliveryInformationData> {
    return await post<DeliveryInformationData, DeliveryInformationPayload>(
      USERS_SERVICE_URLS.DELIVERY_INFORMATION,
      data
    );
  }

  async updateDeliveryInformation(id: number, data: DeliveryInformationPayload): Promise<DeliveryInformationData> {
    return await put<DeliveryInformationData, DeliveryInformationPayload>(
      `${USERS_SERVICE_URLS.DELIVERY_INFORMATION}/${id}`,
      data
    );
  }

  async deleteDeliveryInformation(id: number): Promise<void> {
    await del<void>(`${USERS_SERVICE_URLS.DELIVERY_INFORMATION}/${id}`);
  }
}

const usersServiceInstance = new UsersService();
export default usersServiceInstance;
